// Shapes bucketed rows into the series a chart draws: group them, rank them, fold the
// tail, and lay each series along a fixed axis. Five callers share it — cost per
// service on Cost & Billing at month and at day grain, billing per service on Billing
// at both, and data transfer per SKU — so the fold rule and the gap rule are written
// once and cannot drift between them.
//
// The bucket is the caller's (`bucketOf`), and nothing in here cares what a bucket
// *is*: a month and a day differ only in the string, and the ranking, the top-N fold
// and the gap rule are identical either way. The axis is the caller's too, so the
// months or days a series is silent for stay in place rather than closing up.
//
// Two axes of grouping, and the distinction is the point:
//
//   splitBy  decides which rows may share a y axis at all. Currency for money, unit
//            (and direction) for transfer. Rows that split apart get separate charts,
//            never separate series — putting USD beside SGD, or GB beside TB, invents
//            a comparison the numbers do not support.
//   groupBy  decides the series *within* a chart: the service, the SKU. These do share
//            a scale, so they can be lines together or stacked into a total.
//
// Defaults are the money case (split by currency, group by service) because that is
// where this started; a caller with different fields passes its own accessors.

// Four series is what the validated palette can keep distinguishable under a
// colour-vision deficiency (see global.css). The tail is folded into one "Other"
// series rather than dropped, so a stacked column still adds up to the month's true
// total and a total line still matches the parts beneath it.
const DEFAULT_TOP = 4;

// A bucket with no row is a gap, not a zero: a provider reporting nothing can mean no
// charges or no data retained that far back, and the two do not draw the same. A
// zero would assert the first.
const alongAxis = (axis, buckets) => axis.map((b) => (buckets.has(b) ? buckets.get(b) : null));

export function monthlyCharts(
  rows,
  {
    axis = [],
    splits,
    splitBy = (r) => r.currency,
    groupBy = (r) => r.service,
    labelOf = groupBy,
    valueOf = (r) => r.cost,
    // Which field puts a row on the axis. Defaults to the month because that is where
    // this started; the daily endpoints key on `date`, and swapping this is the whole
    // of what those charts need — everything below is grain-agnostic.
    bucketOf = (r) => r.month,
    fallbackCurrency = 'USD',
    topN = DEFAULT_TOP,
    // What the folded tail calls the things it folded: "Other (3 services)".
    groupNoun = 'service',
  } = {}
) {
  // The split comes first, and nothing below crosses it.
  const bySplit = new Map();
  for (const r of rows) {
    const split = splitBy(r);
    const block = bySplit.get(split) || { groups: new Map(), totals: new Map() };
    const group = groupBy(r);
    const entry = block.groups.get(group) || { label: labelOf(r), months: new Map() };
    const value = valueOf(r) || 0;
    const bucket = bucketOf(r);
    entry.months.set(bucket, (entry.months.get(bucket) || 0) + value);
    block.groups.set(group, entry);
    block.totals.set(bucket, (block.totals.get(bucket) || 0) + value);
    bySplit.set(split, block);
  }

  // A split with nothing to draw is not a chart. Two ways that happens, and both put
  // an axis on screen that says nothing: the caller's `splits` order names a key that
  // has no rows at all, or a key whose every row is zero — free-tier and
  // credit-covered usage costs nothing, and OCI reports plenty of it. A reader cannot
  // tell such a chart from a broken one, so it is left out rather than drawn empty.
  // Nothing is hidden by this: a split with a single non-zero month still qualifies.
  const drawable = (split) => {
    const block = bySplit.get(split);
    if (!block) return false;
    for (const value of block.totals.values()) {
      if (value !== 0) return true;
    }
    return false;
  };

  const order = (splits?.length ? splits : [...bySplit.keys()]).filter(drawable);
  return order.map((currency) => {
    // Present by definition: drawable rejected every key without a block.
    const block = bySplit.get(currency);
    const ranked = [...block.groups.entries()]
      .map(([group, entry]) => ({
        // A series needs a non-empty key to hash its colour from and to key its React
        // node by. The provider names the service on every cost row but the field is
        // optional; two groups cannot both be nameless — they would be one map
        // entry — so this cannot collide.
        key: group || 'unattributed',
        label: entry.label || '—',
        months: entry.months,
        values: alongAxis(axis, entry.months),
        total: [...entry.months.values()].reduce((sum, v) => sum + v, 0),
      }))
      .sort((a, b) => b.total - a.total);
    const tail = ranked.slice(topN);

    return {
      currency,
      // What the chart is *called*, which is not the same as what it is formatted
      // with. A row can arrive with no currency at all — the provider reports the
      // field as optional — and such a group must not be titled with the fallback
      // below: that would put a second chart headed "USD" next to the real one and
      // claim an amount was in dollars when nothing said so.
      label: currency || 'Currency not reported',
      // Intl throws on a blank currency code, so formatting falls back to the page's
      // own currency. This is a formatting concession only, never a label — see above.
      // Callers splitting on something other than currency ignore it.
      code: currency || fallbackCurrency,
      total: {
        key: 'total',
        label: 'Total',
        values: alongAxis(axis, block.totals),
        total: [...block.totals.values()].reduce((sum, v) => sum + v, 0),
      },
      chartServices: [
        ...ranked.slice(0, topN),
        ...(tail.length
          ? [
              {
                key: '__other__',
                label: `Other (${tail.length} ${groupNoun}${tail.length === 1 ? '' : 's'})`,
                // De-emphasis grey, named rather than drawn from the palette: "Other"
                // is not an entity, so it must not hold a hue a real one could have
                // been given.
                color: 'var(--text-muted)',
                values: axis.map((m) =>
                  tail.some((s) => s.months.has(m))
                    ? tail.reduce((sum, s) => sum + (s.months.get(m) || 0), 0)
                    : null
                ),
                total: tail.reduce((sum, s) => sum + s.total, 0),
              },
            ]
          : []),
      ],
      // Every group, unfolded. The top-N cut is a limit of colour, not of the data, so
      // a table view can carry the tail by name.
      services: ranked,
    };
  });
}
